// @ts-check
import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ErrorMessage as BaseErrorMessage, Field, Form, Formik } from 'formik';
import React from 'react';
import * as yup from 'yup';
import { Box, Flex, Label, Text, TextInput } from '~/design-system';
import CountriesAutocomplete from '../CountriesAutocomplete';

const EMPTY = '(empty)';

/**
 *
 * @param {object} props
 * @param {object} props.initialValues
 * @param {(formikContext: object) => void} props.setFormikContext
 * @param {(companyData: object) => void} props.onInviteCompany
 * @returns
 */
export default function AddCompany({
  initialValues,
  setFormikContext,
  onInviteCompany,
}) {
  return (
    <React.Fragment>
      <Flex sx={{ margin: '0 auto' }}>
        <Flex
          direction="column"
          sx={{ width: ['100vw', '80vw', 300], paddingX: 2, textAlign: 'left' }}
        >
          <AddNewCompanyContent
            initialValues={initialValues}
            onInviteCompany={onInviteCompany}
            setFormikContext={setFormikContext}
          />
        </Flex>
      </Flex>
    </React.Fragment>
  );
}

/**
 * @param {object} props
 * @param {object} props.initialValues
 * @param {(companyData: object) => void} props.onInviteCompany
 * @param {(formikContext: object) => void} props.setFormikContext
 * @returns
 */
function AddNewCompanyContent({
  initialValues,
  onInviteCompany,
  setFormikContext,
}) {
  const { i18n } = useLingui();
  const schema = yup.object().shape({
    name: yup
      .string()
      .nullable()
      .required(i18n._(t`Please enter the business name.`))
      .trim()
      .min(2, i18n._(t`Please provide at least 2 characters.`)),
    website: yup
      .string()
      .nullable()
      .trim()
      .required(i18n._(t`Please enter the business website.`))
      .matches(
        // eslint-disable-next-line no-useless-escape
        /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/,
        i18n._(t`Please enter a valid url.`)
      ),
    email: yup
      .string()
      .nullable()
      .required(i18n._(t`Please enter a valid email.`))
      .trim()
      .email(i18n._(t`Please enter a valid email.`)),
    country: yup
      .string()
      .nullable()
      .trim()
      .not([EMPTY, null, undefined], i18n._(t`Please select a country.`)),
  });

  return (
    <Formik
      initialValues={{
        name: initialValues?.name || null,
        email: initialValues?.email || null,
        website: initialValues?.website || null,
        country: initialValues?.country || null,
      }}
      onSubmit={onInviteCompany}
      validationSchema={schema}
    >
      {(formikContext) => {
        setFormikContext(formikContext);
        return (
          <Form sx={{ width: '100%' }}>
            <Box>
              <Flex direction="column" sx={{ marginBottom: 3 }}>
                <Label as="label" htmlFor="name">
                  <Trans>Company name</Trans>
                </Label>
                <Field
                  id="name"
                  name="name"
                  as={TextInput}
                  placeholder={i18n._(t`Add the company name`)}
                  sx={{ fontSize: 16, padding: '10px' }}
                />
                <ErrorMessage
                  name="name"
                  component={Error}
                  sx={{ marginBottom: 1 }}
                />
              </Flex>
              <Flex direction="column" sx={{ marginBottom: 3 }}>
                <Label as="label" htmlFor="email">
                  <Trans>Company email</Trans>
                </Label>
                <Field
                  id="email"
                  name="email"
                  as={TextInput}
                  placeholder={i18n._(t`Add the company email`)}
                  sx={{ fontSize: 16, padding: '10px' }}
                />
                <ErrorMessage
                  name="email"
                  component={Error}
                  sx={{ marginBottom: 1 }}
                />
              </Flex>
              <Flex direction="column" sx={{ marginBottom: 3 }}>
                <Label as="label" htmlFor="website">
                  <Trans>Company website</Trans>
                </Label>
                <Field
                  id="website"
                  name="website"
                  as={TextInput}
                  placeholder={i18n._(t`Add the company website`)}
                  sx={{ fontSize: 16, padding: '10px' }}
                />
                <ErrorMessage
                  name="website"
                  component={Error}
                  sx={{ marginBottom: 1 }}
                />
              </Flex>

              <Label as="label" htmlFor="country">
                <Trans>Country</Trans>
              </Label>

              <Field
                id="country"
                name="country"
                onChange={(value) =>
                  formikContext.setFieldValue('country', value?.key || EMPTY)
                }
                as={CountriesAutocomplete}
                sx={{
                  width: '100%',
                  marginBottom: 2,
                }}
              />
              <ErrorMessage
                name="country"
                component={Error}
                sx={{ marginBottom: 1 }}
              />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
}

function ErrorMessage(props) {
  return (
    <BaseErrorMessage
      {...props}
      render={(message) => (
        <Text variant="small" sx={{ color: 'brand.warningRed' }}>
          {message}
        </Text>
      )}
    />
  );
}
